/**
 * KnowledgeBaseTab — Gestión de la base de conocimiento del chatbot.
 * Re-skin estilo Pacientes/SmartFlowsList: header navy, búsqueda dominante,
 * filter chips segmented por categoría, lista en Paper con avatars por categoría.
 * Preserva: state, handlers, API calls, dialog (props: chatbotId, entries, categories, onRefresh).
 */

import React, { useState, useMemo } from "react";
import {
  Box, Typography, Paper, Button, IconButton, TextField, Select, MenuItem,
  Chip, Tooltip, Switch, Alert, Avatar, InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  MedicalServices, Payments, Schedule, LocationOn, Policy,
  Help, Groups, LocalOffer, Healing,
  Storage as StorageIcon,
} from "@mui/icons-material";

import { StyledDialog, inputStyles } from "../../common";

const API_URL = "/api";

const CATEGORY_ICONS = {
  sintomas:    <Healing sx={{ fontSize: 14 }} />,
  servicios:   <MedicalServices sx={{ fontSize: 14 }} />,
  precios:     <Payments sx={{ fontSize: 14 }} />,
  horarios:    <Schedule sx={{ fontSize: 14 }} />,
  ubicacion:   <LocationOn sx={{ fontSize: 14 }} />,
  politicas:   <Policy sx={{ fontSize: 14 }} />,
  faq:         <Help sx={{ fontSize: 14 }} />,
  personal:    <Groups sx={{ fontSize: 14 }} />,
  promociones: <LocalOffer sx={{ fontSize: 14 }} />,
};

// Color suave por categoría (pastel para avatares)
const CATEGORY_COLOR = {
  sintomas:    { bg: "#fef3c7", fg: "#92400e" },
  servicios:   { bg: "#ede9fe", fg: "#5b21b6" },
  precios:     { bg: "#dcfce7", fg: "#166534" },
  horarios:    { bg: "#dbeafe", fg: "#1e40af" },
  ubicacion:   { bg: "#fee2e2", fg: "#991b1b" },
  politicas:   { bg: "#f1f5f9", fg: "#475569" },
  faq:         { bg: "#cffafe", fg: "#155e75" },
  personal:    { bg: "#fce7f3", fg: "#9f1239" },
  promociones: { bg: "#ffedd5", fg: "#9a3412" },
};

const catColor = (id) => CATEGORY_COLOR[id] || { bg: "#f1f5f9", fg: "#475569" };

export default function KnowledgeBaseTab({ chatbotId, entries, categories, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    category: "servicios",
    title: "", content: "", keywords: "",
    priority: 0, enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Nuevo: búsqueda + filtro categoría (estilo Pacientes)
  const [busqueda, setBusqueda] = useState("");
  const [filtroCat, setFiltroCat] = useState("todas");

  // Conteo por categoría
  const conteos = useMemo(() => {
    const map = { todas: entries.length };
    for (const e of entries) {
      map[e.category] = (map[e.category] || 0) + 1;
    }
    return map;
  }, [entries]);

  // Lista filtrada + ordenada por categoría → título
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    let arr = entries.filter((e) => {
      const matchCat = filtroCat === "todas" || e.category === filtroCat;
      if (!matchCat) return false;
      if (!q) return true;
      return (
        e.title?.toLowerCase().includes(q) ||
        e.content?.toLowerCase().includes(q) ||
        (e.keywords || "").toLowerCase().includes(q)
      );
    });
    arr.sort((a, b) => {
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      return (a.title || "").localeCompare(b.title || "");
    });
    return arr;
  }, [entries, busqueda, filtroCat]);

  // Agrupar por categoría para sticky headers
  const grupos = useMemo(() => {
    const map = new Map();
    for (const e of filtradas) {
      if (!map.has(e.category)) map.set(e.category, []);
      map.get(e.category).push(e);
    }
    return Array.from(map.entries());
  }, [filtradas]);

  const openCreate = () => {
    setEditingEntry(null);
    setFormData({
      category: filtroCat !== "todas" ? filtroCat : "servicios",
      title: "", content: "", keywords: "",
      priority: 0, enabled: true,
    });
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      content: entry.content,
      keywords: entry.keywords || "",
      priority: entry.priority,
      enabled: entry.enabled,
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const url = editingEntry
        ? `${API_URL}/smart-flows/${chatbotId}/knowledge/${editingEntry.id}`
        : `${API_URL}/smart-flows/${chatbotId}/knowledge`;
      const response = await fetch(url, {
        method: editingEntry ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Error guardando entrada");
      }
      setDialogOpen(false);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("¿Eliminar esta entrada?")) return;
    try {
      const response = await fetch(
        `${API_URL}/smart-flows/${chatbotId}/knowledge/${entryId}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Error eliminando entrada");
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e3a5f", mb: 0.3 }}>
            Base de conocimiento
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            {filtradas.length} de {entries.length} entradas
            {filtroCat !== "todas" ? ` · categoría: ${categories.find((c) => c.id === filtroCat)?.name || filtroCat}` : ""}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
            borderRadius: 2,
            px: 2.5, py: 0.8,
            fontSize: 13, fontWeight: 700, textTransform: "none",
            boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)" },
          }}
        >
          Nueva entrada
        </Button>
      </Box>

      {/* Búsqueda dominante */}
      <TextField
        fullWidth
        placeholder="Buscar por título, contenido o palabra clave..."
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5, background: "#fff", fontSize: 14,
            "& fieldset": { borderColor: "#e2e8f0" },
            "&:hover fieldset": { borderColor: "#cbd5e1" },
            "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 2 },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#94a3b8" }} />
            </InputAdornment>
          ),
          endAdornment: busqueda && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setBusqueda("")}>
                <ClearIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Filter chips segmented por categoría */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 1.5 }}>
        <CategoryChip id="todas" label="Todas" count={conteos.todas || 0} activo={filtroCat === "todas"} onClick={() => setFiltroCat("todas")} />
        {categories.map((cat) => {
          const count = conteos[cat.id] || 0;
          if (count === 0 && filtroCat !== cat.id) return null;
          return (
            <CategoryChip
              key={cat.id}
              id={cat.id}
              label={cat.name}
              count={count}
              activo={filtroCat === cat.id}
              onClick={() => setFiltroCat(cat.id)}
            />
          );
        })}
      </Box>

      {/* Lista */}
      {entries.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: "center", borderRadius: 3,
          border: "1px dashed #cbd5e1", background: "#f8fafc",
        }}>
          <StorageIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ color: "#475569", fontSize: 14, fontWeight: 600, mb: 0.5 }}>
            Aún no hay entradas en la base
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: 12, mb: 2 }}>
            Agrega información que el chatbot pueda usar al responder
          </Typography>
          <Button
            variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
              boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)" },
            }}
          >
            Agregar primera entrada
          </Button>
        </Paper>
      ) : filtradas.length === 0 ? (
        <Paper sx={{
          p: 5, textAlign: "center", borderRadius: 3,
          border: "1px dashed #cbd5e1", background: "#f8fafc",
        }}>
          <Typography sx={{ color: "#475569", fontSize: 13, fontWeight: 600 }}>
            Sin resultados para "{busqueda}"
          </Typography>
          <Button
            size="small" onClick={() => { setBusqueda(""); setFiltroCat("todas"); }}
            sx={{ mt: 1, textTransform: "none", fontSize: 12 }}
          >
            Limpiar filtros
          </Button>
        </Paper>
      ) : (
        <Paper sx={{
          borderRadius: 3, overflow: "hidden",
          border: "1px solid #e2e8f0", background: "#fff",
        }}>
          {grupos.map(([catId, items]) => {
            const cat = categories.find((c) => c.id === catId);
            return (
              <React.Fragment key={catId}>
                {/* Sticky header de categoría */}
                <Box sx={{
                  position: "sticky", top: 0, zIndex: 2,
                  px: 2, py: 0.7,
                  background: "linear-gradient(180deg, #f8fafc, #f1f5f9)",
                  borderBottom: "1px solid #e2e8f0",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex", alignItems: "center", gap: 0.75,
                }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: 0.85,
                    background: catColor(catId).bg, color: catColor(catId).fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    "& svg": { fontSize: 12 },
                  }}>
                    {CATEGORY_ICONS[catId] || <Help sx={{ fontSize: 12 }} />}
                  </Box>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 800, color: "#475569",
                    letterSpacing: 1.2, textTransform: "uppercase",
                  }}>
                    {cat?.name || catId}  ·  {items.length}
                  </Typography>
                </Box>
                {items.map((entry) => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    onEdit={() => openEdit(entry)}
                    onDelete={() => handleDelete(entry.id)}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </Paper>
      )}

      {/* Dialog crear/editar */}
      <StyledDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingEntry ? "Editar entrada" : "Nueva entrada"}
        icon={<StorageIcon />}
        variant="compact"
        maxWidth="sm"
      >
        <StyledDialog.Body compact>
          {error && (
            <Alert severity="error" sx={{ py: 0.5, fontSize: 12 }}>{error}</Alert>
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#1e3a5f", fontWeight: 600, mb: 0.5 }}>Categoría</Typography>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                size="small" fullWidth
                sx={{
                  borderRadius: 2, bgcolor: "#fff", fontSize: 13,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: catColor(cat.id).fg }}>{CATEGORY_ICONS[cat.id]}</Box>
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", pb: 0.5 }}>
              <Box
                onClick={() => setFormData({ ...formData, enabled: !formData.enabled })}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.5, py: 0.75, borderRadius: 2, cursor: "pointer",
                  bgcolor: formData.enabled ? "#f0fdf4" : "#f1f5f9",
                  border: formData.enabled ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                }}
              >
                <Switch
                  checked={formData.enabled} size="small"
                  sx={{
                    width: 28, height: 16, p: 0,
                    "& .MuiSwitch-switchBase": { p: 0.25,
                      "&.Mui-checked": { transform: "translateX(12px)", color: "#22c55e",
                        "& + .MuiSwitch-track": { bgcolor: "#22c55e", opacity: 0.5 } } },
                    "& .MuiSwitch-thumb": { width: 12, height: 12 },
                    "& .MuiSwitch-track": { borderRadius: 8, bgcolor: "#cbd5e1" },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: formData.enabled ? "#16a34a" : "#64748b", fontWeight: 600 }}>
                  {formData.enabled ? "Activa" : "Inactiva"}
                </Typography>
              </Box>
            </Box>
          </Box>
          <TextField
            fullWidth size="small" label="Título"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Ej: Horario de atención"
            sx={inputStyles}
          />
          <TextField
            fullWidth multiline rows={4} label="Contenido"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="Información que el asistente usará para responder..."
            sx={inputStyles}
          />
          <Box>
            <TextField
              fullWidth size="small" label="Palabras clave (opcional)"
              value={formData.keywords}
              onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              placeholder="horario, atención, horas (separadas por coma)"
              sx={inputStyles}
            />
            <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.4 }}>
              Ayudan a encontrar esta entrada más rápido
            </Typography>
          </Box>
        </StyledDialog.Body>

        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDialogOpen(false)} />
          <StyledDialog.PrimaryButton
            fullWidth onClick={handleSave}
            disabled={!formData.title || !formData.content}
            loading={saving}
          >
            Guardar
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}

// ─── Componentes auxiliares ───

function CategoryChip({ id, label, count, activo, onClick }) {
  const col = catColor(id);
  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {id !== "todas" && (
            <Box sx={{ color: activo ? "rgba(255,255,255,0.85)" : col.fg, display: "flex" }}>
              {CATEGORY_ICONS[id]}
            </Box>
          )}
          <span>{label}</span>
          <Box sx={{
            fontSize: 10, fontWeight: 700,
            bgcolor: activo ? "rgba(255,255,255,0.25)" : "#e2e8f0",
            color: activo ? "#fff" : "#475569",
            px: 0.7, py: 0.05, borderRadius: 1,
          }}>
            {count}
          </Box>
        </Box>
      }
      size="small"
      onClick={onClick}
      sx={{
        fontSize: 12, fontWeight: 600, height: 28, cursor: "pointer",
        color: activo ? "#fff" : "#475569",
        background: activo ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)" : "#fff",
        border: activo ? "none" : "1px solid #e2e8f0",
        "&:hover": {
          background: activo
            ? "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)"
            : "#f8fafc",
        },
      }}
    />
  );
}

function EntryRow({ entry, onEdit, onDelete }) {
  const col = catColor(entry.category);
  const disabled = !entry.enabled;
  return (
    <Box
      onClick={onEdit}
      sx={{
        display: "flex", alignItems: "flex-start", gap: 1.25,
        px: 2, py: 1.2,
        cursor: "pointer",
        borderBottom: "1px solid #f1f5f9",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.15s",
        "&:last-child": { borderBottom: "none" },
        "&:hover": {
          background: "#f8fafc",
          "& .entry-actions": { opacity: 1 },
        },
      }}
    >
      <Avatar sx={{
        width: 30, height: 30,
        bgcolor: col.bg, color: col.fg,
        fontSize: 13, flexShrink: 0, mt: 0.25,
      }}>
        {CATEGORY_ICONS[entry.category] || <Help sx={{ fontSize: 14 }} />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.2 }}>
          <Typography sx={{
            fontSize: 13.5, fontWeight: 700, color: "#1e293b",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: { xs: 180, sm: 320, md: 460 },
          }}>
            {entry.title}
          </Typography>
          {disabled && (
            <Chip size="small" label="Inactiva" sx={{
              height: 17, fontSize: 9.5, fontWeight: 700,
              bgcolor: "#f1f5f9", color: "#64748b",
            }}/>
          )}
        </Box>
        <Typography sx={{
          color: "#64748b", fontSize: 12, lineHeight: 1.45,
          overflow: "hidden", textOverflow: "ellipsis",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}>
          {entry.content}
        </Typography>
        {entry.keywords && (
          <Box sx={{ display: "flex", gap: 0.4, mt: 0.6, flexWrap: "wrap" }}>
            {entry.keywords.split(",").slice(0, 4).map((kw, i) => (
              <Box key={i} sx={{
                px: 0.7, py: 0.1, borderRadius: 0.85,
                background: "#f1f5f9", border: "1px solid #e2e8f0",
                fontSize: 10, color: "#64748b", fontWeight: 600,
              }}>
                {kw.trim()}
              </Box>
            ))}
            {entry.keywords.split(",").length > 4 && (
              <Box sx={{
                px: 0.7, py: 0.1, borderRadius: 0.85,
                background: "#eff6ff", border: "1px solid #bfdbfe",
                fontSize: 10, color: "#2563eb", fontWeight: 700,
              }}>
                +{entry.keywords.split(",").length - 4}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box className="entry-actions" sx={{
        display: "flex", gap: 0.25, opacity: 0,
        transition: "opacity 0.15s", flexShrink: 0,
      }}>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }}
            sx={{ color: "#cbd5e1", p: 0.5, "&:hover": { color: "#1e3a5f", bgcolor: "#eef2ff" } }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{ color: "#cbd5e1", p: 0.5, "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
